import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useToken } from '../../../Component/Context/useToken';
import '../../../Component/UI/adminTheme.css';
import logo from '../../../Image/Logo.png';
import { BROADCAST_APP_STORE_URLS, getBroadcastAppInstallUrl } from './broadcastAppInstall';

const stores = [
  { url: BROADCAST_APP_STORE_URLS.ios, name: 'App Store', device: 'iPhone · iPad' },
  { url: BROADCAST_APP_STORE_URLS.android, name: 'Google Play', device: 'Android' },
];

export default function BroadcastAppDownload() {
  const { logout } = useToken();
  const preferredUrl = getBroadcastAppInstallUrl();
  const orderedStores = [...stores].sort((a, b) => Number(b.url === preferredUrl) - Number(a.url === preferredUrl));

  return <Page className="admin-layout">
    <Header>
      <span>팬버드 <small>셀러 관리자</small></span>
      <button type="button" onClick={() => logout('ok')}>로그아웃</button>
    </Header>
    <Content>
      <img className="app-logo" src={logo} width="88" height="88" alt="팬버드" />
      <p className="eyebrow">팬버드 송출 앱</p>
      <h1>라이브 방송은<br /><strong>팬버드 앱에서</strong></h1>
      <p className="description">방송 목록 확인부터 리허설, 라이브 송출까지.<br />모바일에서는 팬버드 앱으로 시작해 주세요.</p>
      <Downloads aria-label="팬버드 앱 다운로드">
        {orderedStores.map(store => <a key={store.url} href={store.url}
          className={store.url === preferredUrl ? 'primary' : undefined}
          aria-label={`${store.name}에서 팬버드 앱 다운로드`}>
          <span><small>{store.device}</small><strong>{store.name}에서 다운로드</strong></span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12m-5-5 5 5 5-5M4 16v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
          </svg>
        </a>)}
      </Downloads>
      <p className="account-note">설치 후 <strong>기존 셀러 계정</strong>으로 로그인해 주세요.</p>
    </Content>
    <Footer><Link to="/admin/livelist">라이브 목록으로 돌아가기</Link><p>PC에서는 웹 관리자에서 라이브를 관리할 수 있어요.</p></Footer>
  </Page>;
}

const Page = styled.div`
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--admin-canvas);
  color: var(--admin-ink);
  font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-regular);
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
  *, *::before, *::after { box-sizing: border-box; }
  button, a { -webkit-tap-highlight-color: transparent; }
  button:focus-visible, a:focus-visible {
    outline: 3px solid var(--admin-theme-focus, var(--admin-accent));
    outline-offset: 4px;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(12px + env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 12px max(20px, env(safe-area-inset-left));
  border-bottom: 1px solid var(--admin-border);
  background: var(--admin-surface);
  > span { font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-bold); font-size: 20px; }
  small { display: inline-block; margin-left: 6px; color: var(--admin-muted); font: var(--font-weight-regular) 12px var(--font-family-base), sans-serif; }
  button {
    flex-shrink: 0;
    min-height: 44px;
    padding: 8px 12px;
    border: 1px solid var(--admin-border);
    border-radius: 10px;
    background: var(--admin-surface);
    color: var(--admin-muted);
    font: var(--font-weight-medium) 14px var(--font-family-base), sans-serif;
    cursor: pointer;
    &:active { background: var(--admin-accent-soft); }
  }
`;

const Content = styled.main`
  width: 100%;
  max-width: 440px;
  margin: auto;
  padding: 40px max(24px, env(safe-area-inset-right)) 32px max(24px, env(safe-area-inset-left));
  text-align: center;
  .app-logo { display: block; margin: 0 auto 24px; border-radius: 50%; }
  .eyebrow { margin: 0 0 12px; color: var(--admin-theme-link, var(--admin-accent)); font-size: 14px; font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-semibold); }
  h1 { margin: 0; font: var(--font-weight-bold) 32px/1.35 var(--font-family-base), sans-serif; letter-spacing: -1px; }
  h1 strong { color: var(--admin-theme-link, var(--admin-accent)); font-weight: inherit; }
  .description { margin: 20px 0 32px; color: var(--admin-muted); font-size: 15px; line-height: 1.7; }
  .account-note { margin: 24px 0 0; color: var(--admin-muted); font-size: 13px; line-height: 1.7; }
  .account-note strong { color: var(--admin-ink); font-weight: 600; }
  @media (max-width: 359px) { h1 { font-size: 28px; } }
`;

const Downloads = styled.nav`
  display: grid;
  gap: 12px;
  text-align: left;
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 76px;
    padding: 14px 20px;
    border: 1px solid var(--admin-border);
    border-radius: 14px;
    color: var(--admin-ink);
    background: var(--admin-surface);
    text-decoration: none;
    &:active { background: var(--admin-accent-soft); }
  }
  small { display: block; margin-bottom: 2px; color: var(--admin-muted); font-size: 12px; }
  strong { display: block; font: var(--font-weight-semibold) 16px/1.5 var(--font-family-base), sans-serif; }
  svg { flex-shrink: 0; }
  .primary {
    background: var(--admin-accent);
    border-color: var(--admin-accent);
    color: #fff;
    small { color: #fff; }
    &:active { background: var(--admin-accent-deep); }
  }
`;

const Footer = styled.footer`
  a { display: inline-flex; align-items: center; min-height: 44px; padding: 8px 12px; color: var(--admin-theme-link, var(--admin-accent)); font-size: 14px; text-underline-offset: 4px; }
  p { margin: 8px 0 0; }
  padding: 12px max(24px, env(safe-area-inset-right)) calc(24px + env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left));
  text-align: center;
  color: var(--admin-muted);
  font-size: 12px;
`;
